import React, { useCallback, useMemo } from 'react';

import { Control, FormState, useController, useForm } from 'react-hook-form';

import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, CircularProgress, Container, TextField, Typography } from '@mui/material';

import type { Tag, TagSchedule } from '~/services/TagsService';

import TagAssignments, { type AssignmentFormData } from './TagAssignments/TagAssignments';
import TagContent from './TagContent/TagContent';
import { DEFAULT_TAG_CONTENT_FORM_DATA, type TagContentFormData } from './TagContent/TagContentForm';
import TagFormHeader from './TagFormHeader';
import TagSchedules from './TagSchedules/TagSchedules';

export type { TagContentFormData } from './TagContent/TagContentForm';

export interface FormTag {
  name: string;
  schedules: Array<{
    brands: Array<string>;
    regions: Array<string>;
    activeStartDate: string;
    activeEndDate: string;
  }>;
}

interface Props {
  mode: 'create' | 'edit';
  tag?: Tag;
  tab: string;
  isSaving: boolean;
  control: Control<FormTag>;
  formState?: FormState<FormTag>;
  assignments: Array<AssignmentFormData>;
  onAssignmentsChange: (assignments: Array<AssignmentFormData>) => void;
  initialAssignments?: Array<AssignmentFormData>;
  onBack: () => void;
  onSave: () => void;
  onTabChange: (tab: string) => void;
  contentControl?: Control<TagContentFormData>;
  onContentReset?: (data: TagContentFormData) => void;
  contentError?: string;
}

export default function TagForm({
  mode,
  tag,
  tab,
  isSaving,
  control,
  formState,
  assignments,
  onAssignmentsChange,
  initialAssignments = [],
  onSave,
  onBack,
  onTabChange,
  contentControl: externalContentControl,
  onContentReset: externalContentReset,
  contentError,
}: Props) {
  const {
    field: nameField,
    fieldState: { error: nameError },
  } = useController({
    control,
    name: 'name',
    rules: { required: 'Please enter a tag name' },
  });

  const {
    field: schedulesField,
    fieldState: { error: schedulesError },
  } = useController({
    control,
    name: 'schedules',
    rules: { required: 'Please add at least one schedule' },
  });

  const { control: internalContentControl, reset: internalResetContent } = useForm<TagContentFormData>({
    defaultValues: DEFAULT_TAG_CONTENT_FORM_DATA,
    mode: 'onChange',
  });

  const contentControl = externalContentControl ?? internalContentControl;

  const handleContentReset = useCallback(
    (data: TagContentFormData) => {
      if (externalContentReset !== undefined) {
        externalContentReset(data);
      } else {
        internalResetContent(data, { keepDirty: false });
      }
    },
    [externalContentReset, internalResetContent],
  );

  const handleSchedulesChange = (newSchedules: Array<TagSchedule>) => {
    const formSchedules = newSchedules.map((schedule) => ({
      brands: schedule.brands,
      regions: schedule.regions,
      activeStartDate: schedule.activeStartDate,
      activeEndDate: schedule.activeEndDate,
    }));
    schedulesField.onChange(formSchedules);
  };

  const hasAssignmentChanges = useMemo(() => {
    if (mode !== 'edit' || initialAssignments.length === 0) {
      return false;
    }
    const initialMap = new Map(initialAssignments.map((a) => [`${a.entityType}-${a.entityId}`, a]));
    return assignments.some((a) => !initialMap.has(`${a.entityType}-${a.entityId}`));
  }, [mode, initialAssignments, assignments]);

  const hasChanges = mode === 'edit' && (formState?.dirtyFields.name || hasAssignmentChanges);

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <TagFormHeader
        tab={tab}
        tag={tag}
        isSaving={isSaving}
        onBack={onBack}
        onSave={onSave}
        onTabChange={onTabChange}
      />
      <Box mt={3}>
        {tab === 'assignments' && (
          <>
            <Box mb={4} display="flex" gap={4} alignItems="center">
              <Box flex={0.5}>
                <Typography variant="h6" mb={1} fontWeight={500}>
                  Tag Name
                </Typography>
                <TextField
                  label="Name"
                  value={nameField.value}
                  onChange={nameField.onChange}
                  onBlur={nameField.onBlur}
                  inputRef={nameField.ref}
                  required
                  fullWidth
                  error={!!nameError}
                  disabled={mode === 'edit'}
                  helperText={nameError?.message || 'Unique name for the tag'}
                />
              </Box>
              {mode === 'edit' && tag && (
                <Box mt={2}>
                  <Button
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    color="primary"
                    onClick={onSave}
                    disabled={!hasChanges || isSaving}
                  >
                    Save Edit
                  </Button>
                </Box>
              )}
            </Box>
            <TagAssignments tagId={tag?.id} assignments={assignments} onAssignmentsChange={onAssignmentsChange} />
          </>
        )}
        {tab === 'schedules' && (
          <>
            <TagSchedules
              tagId={tag?.id}
              schedules={(schedulesField.value || []).map((schedule, index) => ({
                ...schedule,
                id: `temp-${index}-${Date.now()}`,
                tagId: tag?.id || '',
              }))}
              onSchedulesChange={handleSchedulesChange}
            />
            {schedulesError && (
              <Typography variant="body2" color="error" mt={1}>
                {schedulesError.message}
              </Typography>
            )}
          </>
        )}
        {tab === 'content' && (
          <TagContent
            mode={mode}
            tagName={nameField.value}
            control={contentControl}
            onReset={handleContentReset}
            error={contentError}
          />
        )}
      </Box>
    </Container>
  );
}
